import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ChevronDown, ChevronUp, Activity } from 'lucide-react';
import { format, parseISO } from 'date-fns';

function formatDate(value) {
  if (!value) return '-';
  try {
    const date = typeof value === 'string' ? parseISO(value) : value;
    return format(date, 'yyyy-MM-dd');
  } catch {
    return '-';
  }
}

function getStatusBadgeVariant(status) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'rolled_over':
      return 'default';
    case 'scheduled':
      return 'secondary';
    case 'pending':
    default:
      return 'secondary';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'completed':
      return '已完成';
    case 'in_progress':
      return '进行中';
    case 'rolled_over':
      return '已滚动';
    case 'scheduled':
      return '已排产';
    case 'pending':
    default:
      return '待开始';
  }
}

function getCompletionCellClass(completed, planned, isSettled) {
  if (!isSettled || !planned || planned <= 0) return 'bg-gray-50';
  const gap = planned - completed;
  if (completed >= planned) return 'bg-green-100 text-green-800';
  if (gap < 5) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

function ProductionScheduleStepTracker({ progress }) {
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  if (!progress || !progress.steps || progress.steps.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        暂无工序步骤
      </div>
    );
  }

  const toggleStep = (sequence) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(sequence)) {
        next.delete(sequence);
      } else {
        next.add(sequence);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Activity className="h-5 w-5" />
          工序进度跟踪
        </h3>
        <span className="text-sm text-muted-foreground">
          计划数量: {progress.planned_quantity || 0}
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 text-center">序号</TableHead>
              <TableHead>工序名称</TableHead>
              <TableHead>设备</TableHead>
              <TableHead className="text-center">标准工时</TableHead>
              <TableHead className="text-center">计划</TableHead>
              <TableHead className="text-center">累计完成</TableHead>
              <TableHead className="text-center">报废</TableHead>
              <TableHead className="w-32">进度</TableHead>
              <TableHead className="text-center">状态</TableHead>
              <TableHead className="text-center">明细</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {progress.steps.map((step) => {
              const isExpanded = expandedSteps.has(step.sequence);
              return (
                <React.Fragment key={step.sequence}>
                  <TableRow
                    className={
                      step.status === 'completed'
                        ? 'bg-green-50'
                        : step.status === 'in_progress'
                        ? 'bg-blue-50'
                        : 'bg-gray-50'
                    }
                  >
                    <TableCell className="text-center font-medium">
                      {step.sequence}
                    </TableCell>
                    <TableCell className="font-medium">{step.name}</TableCell>
                    <TableCell>{step.work_center_name || '-'}</TableCell>
                    <TableCell className="text-center">
                      {step.standard_time || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {step.planned_quantity || 0}
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      {step.cumulative_completed || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      {step.cumulative_scrapped || 0}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Progress value={step.progress_percent || 0} className="h-2" />
                        <span className="text-xs text-muted-foreground">
                          {step.progress_percent || 0}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={getStatusBadgeVariant(step.status)}>
                        {getStatusLabel(step.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStep(step.sequence)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={10} className="p-0">
                        <div className="bg-muted/30 px-4 py-3">
                          <h4 className="text-sm font-semibold mb-2">每日结算明细</h4>
                          {step.history && step.history.length > 0 ? (
                            <div className="rounded-md border bg-white">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-center">日期</TableHead>
                                    <TableHead className="text-center">设备</TableHead>
                                    <TableHead className="text-center">当日计划</TableHead>
                                    <TableHead className="text-center">当日完成</TableHead>
                                    <TableHead className="text-center">当日报废</TableHead>
                                    <TableHead className="text-center">达成</TableHead>
                                    <TableHead className="text-center">状态</TableHead>
                                    <TableHead>结算人</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {step.history.map((entry, idx) => {
                                    const planned = entry.assigned_quantity || 0;
                                    const completed = entry.completed_quantity || 0;
                                    const scrapped = entry.scrapped_quantity || 0;
                                    const isSettled = !!entry.settlement_id;
                                    const cellClass = getCompletionCellClass(completed, planned, isSettled);
                                    return (
                                      <TableRow key={idx}>
                                        <TableCell className="text-center whitespace-nowrap">
                                          {formatDate(entry.date)}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {entry.work_center_name || '-'}
                                        </TableCell>
                                        <TableCell className="text-center">{planned}</TableCell>
                                        <TableCell className={`text-center font-semibold ${cellClass}`}>
                                          {completed}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {scrapped > 0 ? scrapped : '-'}
                                        </TableCell>
                                        <TableCell className="text-center text-xs">
                                          {isSettled && planned > 0
                                            ? `${Math.round((completed / planned) * 100)}%`
                                            : '-'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <Badge variant={getStatusBadgeVariant(entry.status)}>
                                            {getStatusLabel(entry.status)}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs">
                                          {entry.settled_by_name || entry.settled_by || '-'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              暂无排产/结算记录
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ProductionScheduleStepTracker;
